import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _PropTypes from 'prop-types';
import { transparentize } from 'polished';
import * as React from 'react';
import styled from 'styled-components';
import { spacing2x, spacing4x, spacing1x, spacing3x } from '../../styles/spacing.js';
import { ButtonMenu } from '../ButtonMenu/ButtonMenu.js';
import { CardBase } from '../CardBase/CardBase.js';
import { CardDescription } from '../CardBase/CardDescription.js';
import { CardTitle } from '../CardBase/CardTitle.js';
import { Checkbox, CheckboxIcon } from '../Checkbox/Checkbox.js';
import { IconMore } from '../Icons/IconMore.js';
import { statusComponents, Status } from '../Status/Status.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["actions", "buttonMenuProps", "checkboxProps", "descriptionProps", "src", "statusProps", "titleProps"],
    _excluded2 = ["buttonProps"];
var ContentCardOverlayBackground = styled.div.withConfig({
  displayName: "ContentCard__ContentCardOverlayBackground",
  componentId: "sc-1ohx6rk-0"
})(["&&{background-image:url(", ");background-position-x:center;background-position-y:center;background-repeat:no-repeat;background-size:contain;height:100%;left:0;position:absolute;top:0;width:100%;}"], function (props) {
  return props.$src;
});
var ContentCardOverlayBlur = styled.div.withConfig({
  displayName: "ContentCard__ContentCardOverlayBlur",
  componentId: "sc-1ohx6rk-1"
})(["&&{background-color:", ";background-image:url(", ");background-size:cover;filter:blur(80px);height:100%;width:100%;}"], function (props) {
  return props.theme.neutralColors.silver;
}, function (props) {
  return props.$src;
});
var ContentCardOverlayMask = styled.div.withConfig({
  displayName: "ContentCard__ContentCardOverlayMask",
  componentId: "sc-1ohx6rk-2"
})(["&&{background-color:", ";bottom:0;left:0;opacity:0;position:absolute;right:0;top:0;}"], function (props) {
  return transparentize(0.5, props.theme.neutralColors.charcoal);
});
var ContentCardOverlay = styled.div.withConfig({
  displayName: "ContentCard__ContentCardOverlay",
  componentId: "sc-1ohx6rk-3"
})(["&&{border-top-left-radius:", ";border-top-right-radius:", ";height:100%;overflow:hidden;position:relative;width:100%;}"], spacing2x, spacing2x);
var ContentCardCheckbox = styled(Checkbox).withConfig({
  displayName: "ContentCard__ContentCardCheckbox",
  componentId: "sc-1ohx6rk-4"
})(["&&{", ";}"], function (props) {
  return !props.checked && "\n        box-shadow: ".concat("0px 0px 0px 1px ".concat(transparentize(0.6, props.theme.neutralColors.charcoal)), ";\n        ", CheckboxIcon, " {\n            border-color: ").concat(props.theme.neutralColors.white, ";\n        }\n    ");
});
var ContentCardActions = styled.div.withConfig({
  displayName: "ContentCard__ContentCardActions",
  componentId: "sc-1ohx6rk-5"
})(["&&{display:flex;opacity:0;align-items:center;justify-content:center;}&& > div{align-items:center;display:flex;justify-content:center;min-height:32px;min-width:32px;}"]);
var ContentCardCardBase = styled(CardBase).withConfig({
  displayName: "ContentCard__ContentCardCardBase",
  componentId: "sc-1ohx6rk-6"
})(["&&{", ";", ";", ";}&&:hover ", ",&&:focus-within ", "{opacity:1;}&&:hover ", ",&&:focus-within ", "{box-shadow:none;}&&:hover ", ",&&:focus-within ", "{opacity:0;}&&:hover ", ",&&:focus-within ", "{opacity:1;}"], function (props) {
  return props.expired && "\n        ".concat(ContentCardOverlayMask, " {\n            background-color: ").concat(transparentize(0.36, props.theme.neutralColors.silver), ";\n            opacity: 1;\n        }\n    ");
}, function (props) {
  return props.selected && "\n        ".concat(ContentCardCheckbox, " {\n            opacity: 1;\n        }\n    ");
}, function (props) {
  return props.selectable && !props.selected && "\n        :hover,\n        :focus-within {\n            ".concat(ContentCardCheckbox, " {\n                opacity: 1;\n            }\n        }\n    ");
}, ContentCardOverlayMask, ContentCardOverlayMask, ContentCardCheckbox, ContentCardCheckbox, statusComponents.StatusWrapper, statusComponents.StatusWrapper, ContentCardActions, ContentCardActions);
var ContentCard = /*#__PURE__*/React.forwardRef(function ContentCard2(props, ref) {
  var actions = props.actions,
      buttonMenuProps = props.buttonMenuProps,
      checkboxProps = props.checkboxProps,
      descriptionProps = props.descriptionProps,
      _props$src = props.src,
      src = _props$src === void 0 ? "" : _props$src,
      statusProps = props.statusProps,
      titleProps = props.titleProps,
      rest = _objectWithoutProperties(props, _excluded);

  var buttonProps = buttonMenuProps.buttonProps,
      menuProps = _objectWithoutProperties(buttonMenuProps, _excluded2);

  var expired = statusProps.variant === "expired";
  var selectable = !!checkboxProps;
  return /*#__PURE__*/jsx(ContentCardCardBase, _objectSpread(_objectSpread({}, rest), {}, {
    areas: ["a b c d", "e f g h", "i j k l", "m n o p"],
    columns: ["36px", "1fr", "1fr", "52px"],
    rows: ["120px", "auto", "1fr", "52px"],
    items: [{
      area: ["a", "a", "a", "d"],
      children: /*#__PURE__*/jsxs(ContentCardOverlay, {
        children: [/*#__PURE__*/jsx(ContentCardOverlayBlur, {
          $src: src
        }), /*#__PURE__*/jsx(ContentCardOverlayBackground, {
          $src: src
        }), /*#__PURE__*/jsx(ContentCardOverlayMask, {})]
      })
    }, {
      area: "a",
      align: "start",
      justify: "start",
      style: selectable ? {
        lineHeight: 0,
        paddingLeft: spacing4x,
        paddingTop: spacing4x
      } : {},
      children: selectable ? /*#__PURE__*/jsx(ContentCardCheckbox, _objectSpread({}, checkboxProps)) : void 0
    }, {
      area: ["e", "e", "e", "h"],
      align: "start",
      justify: "start",
      style: {
        paddingLeft: spacing4x,
        paddingRight: spacing4x,
        paddingTop: spacing4x
      },
      children: /*#__PURE__*/jsx(CardTitle, _objectSpread(_objectSpread({
        maxLength: 50
      }, titleProps), {}, {
        style: {
          WebkitLineClamp: 2
        }
      }))
    }, {
      area: ["i", "i", "i", "l"],
      align: "start",
      justify: "start",
      style: {
        paddingLeft: spacing4x,
        paddingRight: spacing4x,
        paddingTop: spacing1x
      },
      children: /*#__PURE__*/jsx(CardDescription, _objectSpread({}, descriptionProps))
    }, {
      area: ["m", "m", "m", "o"],
      align: "end",
      justify: "start",
      style: {
        paddingBottom: spacing4x,
        paddingLeft: spacing4x
      },
      children: /*#__PURE__*/jsx(Status, _objectSpread({}, statusProps))
    }, {
      area: ["m", "m", "m", "o"],
      align: "end",
      justify: "end",
      style: {
        paddingBottom: spacing3x,
        paddingLeft: spacing4x
      },
      children: /*#__PURE__*/jsx(ContentCardActions, {
        children: React.Children.map(actions, function (child) {
          return /*#__PURE__*/jsx("div", {
            children: child
          });
        })
      })
    }, {
      area: "p",
      align: "end",
      justify: "end",
      style: {
        paddingLeft: spacing4x,
        paddingBottom: spacing3x,
        paddingRight: spacing3x
      },
      children: /*#__PURE__*/jsx(ButtonMenu, _objectSpread({
        buttonProps: _objectSpread({
          hideLabel: true,
          startAdornment: IconMore,
          variant: "ghost"
        }, buttonProps)
      }, menuProps))
    }],
    expired: expired,
    ref: ref,
    selectable: selectable,
    selected: checkboxProps === null || checkboxProps === void 0 ? void 0 : checkboxProps.checked,
    style: {
      height: descriptionProps.children ? 270 : 226,
      maxWidth: 216
    }
  }));
});
ContentCard.propTypes = {
  "actions": _PropTypes.arrayOf(_PropTypes.element).isRequired,
  "buttonMenuProps": _PropTypes.shape({
    "buttonProps": _PropTypes.object.isRequired,
    "children": _PropTypes.node,
    "containerSelector": _PropTypes.string,
    "inline": _PropTypes.bool,
    "maxHeight": _PropTypes.number,
    "onActivate": _PropTypes.func,
    "onHide": _PropTypes.func,
    "onShow": _PropTypes.func,
    "options": _PropTypes.arrayOf(_PropTypes.object).isRequired,
    "position": _PropTypes.oneOf(["left", "right"]),
    "showing": _PropTypes.bool,
    "tooltipProps": _PropTypes.object,
    "zIndex": _PropTypes.number
  }).isRequired,
  "checkboxProps": _PropTypes.object,
  "descriptionProps": _PropTypes.shape({
    "children": _PropTypes.node
  }).isRequired,
  "src": _PropTypes.string,
  "statusProps": _PropTypes.object.isRequired,
  "titleProps": _PropTypes.object.isRequired
};
ContentCard.displayName = "ContentCard";

export { ContentCard };
